import { useEffect, useMemo, useState } from 'react';
import {
  Handshake, Plus, Download, FileText, Trash2, X, Loader2, Building2,
  Search, RefreshCw, Eye, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import ReportsLayout from '../../../components/reports/ReportsLayout';
import { Stat, Stats } from '../../../components/office';
import api from '@verlo/shared/services/api';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from '@verlo/shared/components/ui/sheet';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';
import { SearchField } from '@verlo/shared/components/ui/search-field';

const fmtMoney = (v) => v === null || v === undefined ? '—' : `£${Math.round(v).toLocaleString('en-GB')}`;
const fmtPct = (v) => v === null || v === undefined ? '—' : `${v > 0 ? '+' : ''}${v}%`;

const EMPTY_FORM = {
  category: '',
  supplier_name: '',
  months: 12,
  format: 'both',
  contact_email: '',
  notes: '',
};

const SupplierReportsPage = () => {
  const [exports, setExports] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [previewExport, setPreviewExport] = useState(null);

  const [form, setForm] = useState(EMPTY_FORM);

  const load = async () => {
    setLoading(true);
    try {
      const [eRes, cRes] = await Promise.all([
        api.get('/reports/supplier/exports', { params: { limit: 40 } }),
        api.get('/reports/supplier/categories'),
      ]);
      setExports(eRes.data.exports);
      setCats(cRes.data.categories);
      if (!form.category && cRes.data.categories[0]) {
        setForm((f) => ({ ...f, category: cRes.data.categories[0].category }));
      }
    } catch {
      toast.error('Could not load supplier exports');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openCreator = (prefill = null) => {
    if (prefill) {
      setForm({
        category: prefill.category || '',
        supplier_name: prefill.supplier_name || '',
        months: prefill.months || 12,
        format: prefill.format || 'both',
        contact_email: prefill.contact_email || '',
        notes: prefill.notes || '',
      });
    } else {
      setForm({ ...EMPTY_FORM, category: cats[0]?.category || '' });
    }
    setCreatorOpen(true);
  };

  const generate = async () => {
    if (!form.supplier_name || !form.category) {
      toast.error('Supplier and category are required');
      return;
    }
    setGenerating(true);
    try {
      await api.post('/reports/supplier/generate', form);
      toast.success(`Brief generated for ${form.supplier_name}`);
      setCreatorOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally { setGenerating(false); }
  };

  const download = (exp, fmt) => {
    const url = `${api.defaults.baseURL}/reports/supplier/exports/${exp.id}/download?fmt=${fmt}`;
    const token = localStorage.getItem('verlo_token');
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const link = document.createElement('a');
        const objUrl = URL.createObjectURL(blob);
        link.href = objUrl;
        link.download = `verlo_${exp.supplier_name.replace(/\s+/g, '_')}_${exp.category}.${fmt}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objUrl);
      })
      .catch(() => toast.error('Download failed'));
  };

  const remove = async (id) => {
    try {
      await api.delete(`/reports/supplier/exports/${id}`);
      toast.success('Export deleted');
      await load();
    } catch { toast.error("Couldn't delete"); }
  };

  const totalRev = useMemo(() => exports.reduce((sum, e) => sum + (e.summary?.current_revenue || 0), 0), [exports]);

  const filteredExports = useMemo(() => {
    const q = search.trim().toLowerCase();
    return exports.filter((e) => {
      if (categoryFilter !== 'all' && e.category !== categoryFilter) return false;
      if (!q) return true;
      const hay = `${e.supplier_name} ${e.category} ${e.notes || ''} ${e.contact_email || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [exports, search, categoryFilter]);

  return (
    <ReportsLayout>
      <div data-testid="supplier-reports-page">
        {/* Header */}
        <div className="flex min-h-[36px] items-center justify-between flex-wrap gap-3 mb-5">
          <div>
            <p className="text-[14px] text-ink-soft max-w-2xl">
              A brief to hand a supplier: how their category sold, its premium mix and the year before, as a PDF and a CSV.
            </p>
          </div>
          <Button onClick={() => openCreator()} className="bg-verlo hover:bg-verlo-hover gap-1.5" data-testid="supplier-generate-btn">
            <Plus size={13} />
            Generate brief
          </Button>
        </div>

        <Stats className="mb-5">
          <Stat label="Briefs made" value={exports.length} />
          <Stat label="Takings they cover" value={fmtMoney(totalRev)} />
          <Stat label="Categories to choose from" value={cats.length} />
        </Stats>

        {/* Search + filter toolbar */}
        <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="supplier-toolbar">
          <SearchField
            className="flex-1 min-w-[260px] max-w-md"
            placeholder="Search supplier, category, notes…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClear={() => setSearch('')}
            clearTestId="supplier-search-clear"
            data-testid="supplier-search"
          />
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="h-9 w-[200px]" data-testid="supplier-category-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {cats.map((c) => (
                <SelectItem key={c.category} value={c.category}>{c.category}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(search || categoryFilter !== 'all') && (
            <span className="text-[12.5px] text-ink-soft ml-1">
              {filteredExports.length} of {exports.length} shown
            </span>
          )}
        </div>

        {/* Exports list */}
        <div className="bg-white border border-hairline rounded-lg overflow-hidden" data-testid="supplier-exports-list">
          <div className="px-5 py-3 border-b border-hairline">
            <h3 className="text-sm font-semibold text-ink">Recent briefs</h3>
            <p className="text-[12.5px] text-ink-soft mt-0.5">Each export contains a PDF for the meeting and a CSV for the supplier&apos;s analyst</p>
          </div>
          {loading && <div className="px-5 py-12 text-center text-ink-faint text-sm">Loading…</div>}
          {!loading && exports.length === 0 && (
            <div className="px-5 py-16 text-center">
              <Handshake size={28} className="mx-auto text-ink-faint mb-3" />
              <p className="text-sm text-ink-soft">No briefs yet.</p>
              <p className="text-[12px] text-ink-faint mt-1">Click <strong>Generate brief</strong> to build your first.</p>
            </div>
          )}
          {!loading && exports.length > 0 && filteredExports.length === 0 && (
            <div className="px-5 py-12 text-center">
              <Search size={24} className="mx-auto text-ink-faint mb-3" />
              <p className="text-[13px] text-ink-soft">No briefs match your search.</p>
              <button onClick={() => { setSearch(''); setCategoryFilter('all'); }} className="mt-2 text-[12px] text-verlo hover:underline font-semibold" data-testid="supplier-clear-filters">Clear filters</button>
            </div>
          )}
          {!loading && filteredExports.length > 0 && (
            <div className="divide-y divide-hairline">
              {filteredExports.map((e) => (
                <div key={e.id} className="px-5 py-4 hover:bg-hover-tint/60 group" data-testid={`supplier-row-${e.id}`}>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-paper border border-hairline flex items-center justify-center shrink-0">
                      <Building2 size={16} className="text-ink-soft" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <h4 className="text-sm font-semibold text-ink">{e.supplier_name}</h4>
                        <span className="text-[12.5px] text-ink-soft">·</span>
                        <span className="text-[12px] text-ink-soft capitalize">{e.category}</span>
                        <span className="text-[12.5px] text-ink-faint">·</span>
                        <span className="text-[12.5px] text-ink-soft font-mono tabular-nums">{e.months}mo window</span>
                      </div>
                      <div className="flex items-baseline gap-3 mt-1 text-[12.5px] text-ink-soft flex-wrap">
                        <span className="font-mono tabular-nums">{fmtMoney(e.summary?.current_revenue)} revenue</span>
                        <span>·</span>
                        <span className="font-mono tabular-nums">{(e.summary?.current_units || 0).toLocaleString('en-GB')} units</span>
                        <span>·</span>
                        <span className="font-mono tabular-nums">{e.summary?.premium_pct ?? 0}% premium mix</span>
                        {e.summary?.revenue_delta_pct !== null && e.summary?.revenue_delta_pct !== undefined && (
                          <>
                            <span>·</span>
                            <span className={`font-mono tabular-nums ${e.summary.revenue_delta_pct >= 0 ? 'text-signal-ok' : 'text-signal-stop'}`}>
                              {fmtPct(e.summary.revenue_delta_pct)} vs prev
                            </span>
                          </>
                        )}
                      </div>
                      {e.narrative && (
                        <p className="mt-2 text-[12px] text-ink-soft leading-relaxed line-clamp-2">{e.narrative}</p>
                      )}
                      <div className="mt-1 text-[12.5px] text-ink-faint font-mono tabular-nums">
                        Generated {e.generated_at?.slice(0, 19).replace('T', ' ')} by {e.generated_by_name}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                      <button onClick={() => setPreviewExport(e)} className="inline-flex items-center gap-1 text-[12.5px] font-semibold px-2.5 py-1.5 rounded-md border border-hairline hover:border-ink-faint" data-testid={`supplier-preview-${e.id}`} title="What's inside">
                        <Eye size={11} />Preview
                      </button>
                      <button onClick={() => openCreator(e)} className="inline-flex items-center gap-1 text-[12.5px] font-semibold px-2.5 py-1.5 rounded-md border border-hairline hover:border-ink-faint" data-testid={`supplier-rerun-${e.id}`} title="Re-run with new window">
                        <RefreshCw size={11} />Re-run
                      </button>
                      {e.pdf_bytes && (
                        <button onClick={() => download(e, 'pdf')} className="inline-flex items-center gap-1 text-[12.5px] font-semibold px-2.5 py-1.5 rounded-md bg-verlo text-white hover:bg-verlo-hover" data-testid={`supplier-pdf-${e.id}`}>
                          <Download size={11} />PDF
                        </button>
                      )}
                      {e.csv_bytes && (
                        <button onClick={() => download(e, 'csv')} className="inline-flex items-center gap-1 text-[12.5px] font-semibold px-2.5 py-1.5 rounded-md border border-hairline hover:border-ink-faint" data-testid={`supplier-csv-${e.id}`}>
                          <FileText size={11} />CSV
                        </button>
                      )}
                      <button onClick={() => remove(e.id)} className="inline-flex items-center justify-center w-7 h-7 rounded-md text-ink-faint hover:text-signal-stop hover:bg-signal-stop-tint" data-testid={`supplier-delete-${e.id}`}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* "What's inside" preview sheet */}
        <Sheet open={!!previewExport} onOpenChange={(o) => !o && setPreviewExport(null)}>
          <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="supplier-preview-sheet">
            <SheetHeader>
              <SheetTitle>What&apos;s inside this brief</SheetTitle>
              <SheetDescription>Preview the headline KPIs, Verlo Insight narrative and download options before sending.</SheetDescription>
            </SheetHeader>
            {previewExport && (
              <div className="mt-6 space-y-5">
                {/* Brand header */}
                <div className="border-b border-hairline pb-4">
                  <div className="font-heading text-[11px] font-bold uppercase tracking-[0.06em] text-ink-faint">Supplier brief</div>
                  <div className="font-heading text-[18px] font-extrabold text-ink mt-1">{previewExport.supplier_name}</div>
                  <div className="text-[13px] text-ink-soft mt-0.5 capitalize">{previewExport.category} · {previewExport.months} months</div>
                </div>

                {/* Headline KPIs mirror the PDF */}
                <div>
                  <div className="font-heading text-[11px] font-bold uppercase tracking-[0.06em] text-ink-faint mb-2">Headline performance</div>
                  <div className="space-y-2">
                    <PreviewRow label="Total revenue" value={fmtMoney(previewExport.summary?.current_revenue)} hint={previewExport.summary?.revenue_delta_pct !== null && previewExport.summary?.revenue_delta_pct !== undefined ? `${fmtPct(previewExport.summary.revenue_delta_pct)} vs prev` : null} tone={previewExport.summary?.revenue_delta_pct >= 0 ? 'good' : 'bad'} />
                    <PreviewRow label="Units sold" value={(previewExport.summary?.current_units || 0).toLocaleString('en-GB')} hint={previewExport.summary?.units_delta_pct !== null && previewExport.summary?.units_delta_pct !== undefined ? `${fmtPct(previewExport.summary.units_delta_pct)} vs prev` : null} tone={previewExport.summary?.units_delta_pct >= 0 ? 'good' : 'bad'} />
                    <PreviewRow label="Premium mix" value={`${previewExport.summary?.premium_pct ?? 0}%`} hint="Premiumisation indicator" />
                    <PreviewRow label="Products covered" value={previewExport.summary?.product_count ?? 0} />
                  </div>
                </div>

                {/* Narrative */}
                {previewExport.narrative && (
                  <div>
                    <div className="inline-flex items-center gap-1.5 font-heading text-[11px] font-bold uppercase tracking-[0.06em] text-ink-faint mb-2">
                      <Sparkles size={11} className="text-verlo" /> Verlo Insight
                    </div>
                    <div className="bg-surface-2 border border-hairline rounded-lg p-4">
                      <p className="text-[13px] text-ink-soft leading-relaxed">{previewExport.narrative}</p>
                    </div>
                  </div>
                )}

                {/* Metadata */}
                <div>
                  <div className="font-heading text-[11px] font-bold uppercase tracking-[0.06em] text-ink-faint mb-2">Details</div>
                  <div className="space-y-1.5 text-[12px]">
                    <DetailRow label="Generated" value={previewExport.generated_at?.slice(0, 19).replace('T', ' ')} />
                    <DetailRow label="By" value={previewExport.generated_by_name} />
                    <DetailRow label="Format" value={previewExport.format?.toUpperCase()} />
                    {previewExport.contact_email && <DetailRow label="Contact" value={previewExport.contact_email} />}
                    {previewExport.notes && <DetailRow label="Notes" value={previewExport.notes} />}
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex gap-2 pt-3 border-t border-hairline">
                  {previewExport.pdf_bytes && (
                    <Button onClick={() => download(previewExport, 'pdf')} className="flex-1 bg-verlo hover:bg-verlo-hover" data-testid="supplier-preview-download-pdf">
                      <Download size={13} className="mr-1.5" />Download PDF
                    </Button>
                  )}
                  {previewExport.csv_bytes && (
                    <Button variant="outline" onClick={() => download(previewExport, 'csv')} className="flex-1" data-testid="supplier-preview-download-csv">
                      <FileText size={13} className="mr-1.5" />Download CSV
                    </Button>
                  )}
                </div>
              </div>
            )}
          </SheetContent>
        </Sheet>

        {/* Generate drawer */}
        <Sheet open={creatorOpen} onOpenChange={setCreatorOpen}>
          <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="supplier-creator-sheet">
            <SheetHeader>
              <SheetTitle>Generate supplier brief</SheetTitle>
              <SheetDescription>Configure the supplier, category and window. Verlo renders a branded PDF and CSV ready to send.</SheetDescription>
            </SheetHeader>
            <div className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label className="text-[12px]">Supplier name</Label>
                <Input
                  placeholder="e.g. Acme Beef Co"
                  value={form.supplier_name}
                  onChange={(e) => setForm((f) => ({ ...f, supplier_name: e.target.value }))}
                  data-testid="supplier-form-name"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[12px]">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger data-testid="supplier-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {cats.map((c) => (
                      <SelectItem key={c.category} value={c.category}>
                        {c.category} <span className="text-ink-faint ml-1">· {c.item_count} items</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[12px]">Window (months)</Label>
                  <Select value={String(form.months)} onValueChange={(v) => setForm((f) => ({ ...f, months: Number(v) }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">Last 3 months</SelectItem>
                      <SelectItem value="6">Last 6 months</SelectItem>
                      <SelectItem value="12">Last 12 months</SelectItem>
                      <SelectItem value="24">Last 24 months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[12px]">Format</Label>
                  <Select value={form.format} onValueChange={(v) => setForm((f) => ({ ...f, format: v }))}>
                    <SelectTrigger data-testid="supplier-form-format"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="both">PDF + CSV</SelectItem>
                      <SelectItem value="pdf">PDF only</SelectItem>
                      <SelectItem value="csv">CSV only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[12px]">Contact email <span className="text-ink-faint">· optional</span></Label>
                <Input
                  type="email"
                  placeholder="e.g. buyer@acmebeef.com"
                  value={form.contact_email}
                  onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[12px]">Notes <span className="text-ink-faint">· optional</span></Label>
                <Input
                  placeholder="Negotiation context, asks, anything to remember"
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                />
              </div>
              <div className="bg-surface-2 border border-hairline rounded-lg p-3 text-[12.5px] text-ink-soft leading-relaxed">
                Verlo will pull the last {form.months} months of trade in <strong>{form.category || 'this category'}</strong>, score the premium-mix movement, write a Verlo Insight narrative, and render a branded PDF you can hand straight to the buyer.
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setCreatorOpen(false)} className="flex-1" data-testid="supplier-form-cancel">
                  <X size={13} className="mr-1" />Cancel
                </Button>
                <Button onClick={generate} disabled={generating} className="flex-1 bg-verlo hover:bg-verlo-hover" data-testid="supplier-form-submit">
                  {generating ? <Loader2 size={13} className="animate-spin mr-1" /> : <Plus size={13} className="mr-1" />}
                  {generating ? 'Generating…' : 'Generate'}
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </ReportsLayout>
  );
};

const PreviewRow = ({ label, value, hint, tone }) => {
  const toneClass = tone === 'good' ? 'text-signal-ok' : tone === 'bad' ? 'text-signal-stop' : 'text-ink-soft';
  return (
    <div className="flex items-baseline justify-between gap-3 px-3 py-2.5 bg-white border border-hairline rounded-md">
      <span className="font-heading text-[11px] font-bold uppercase tracking-[0.06em] text-ink-faint">{label}</span>
      <div className="text-right">
        <div className="font-mono text-[15px] text-ink tabular-nums">{value}</div>
        {hint && <div className={`text-[12.5px] ${toneClass} font-mono tabular-nums mt-0.5`}>{hint}</div>}
      </div>
    </div>
  );
};

const DetailRow = ({ label, value }) => (
  <div className="flex items-baseline gap-3">
    <span className="text-[12.5px] text-ink-soft w-20 shrink-0">{label}</span>
    <span className="text-[12px] text-ink-soft break-all">{value}</span>
  </div>
);

export default SupplierReportsPage;
